/**
 * Group Template Editor — one group, its items, each item's subitems, and
 * updates at both levels (requirements F2.4–F2.6).
 *
 * Same contract as the subitem editor: the whole screen is a DRAFT that touches
 * the server only on Save (POST /templates/save with type 'group' and the
 * content from lib/groupContent). Items are authored against the current item's
 * board columns, subitems against its subitems board columns, through the same
 * table, cell editors, column menus and updates panel the subitem editor uses —
 * only the level they act on differs.
 */
import { ArrowLeft, Download, Loader2, RefreshCw, Save } from 'lucide-react';
import { Button, Modal, ModalContent, ModalFooter } from 'monday-ui-react-core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { canonicalType } from '../../Config';
import { useAccountPeople } from '../../lib/accountPeople';
import { captureGroup, importErrorNote, truncationNote, UPDATES_REFUSED_NOTE } from '../../lib/captureGroup';
import { resolveColumns } from '../../lib/columnMatching';
import { serializeRowValues } from '../../lib/columnValues';
import { contentFromDraft, countContent, draftFromContent, emptyItemDraft, emptySubitemDraft } from '../../lib/groupContent';
import { blankUpdateGroupKeys, blankUpdatesMessage, unnamedGroupKeys, unnamedRowsMessage } from '../../lib/rowNames';
import { isNoSubitemsBoard, NO_SUBITEMS_BOARD_HINT } from '../../lib/subitemsBoard';
import { uniqueTemplateName } from '../../lib/templateNames';
import { GROUP_LIMITS } from '../../lib/templateLimits';
import useBoardColumns, { useBoardMembers } from '../../lib/useBoardColumns';
import useItemBoardColumns from '../../lib/useItemBoardColumns';
import SubitemsTable from '../SubitemsTable';
import AddColumnMenu from '../TemplateEditor/AddColumnMenu';
import CellEditor from '../TemplateEditor/CellEditor';
import ColumnMenu from '../TemplateEditor/ColumnMenu';
import TemplateDetailsCard from '../TemplateEditor/TemplateDetailsCard';
import UpdatesPanel from '../TemplateEditor/UpdatesPanel';
import { TokenText } from '../DynamicTextField';
import GroupHeaderCard from './GroupHeaderCard';
import GroupPicker from './GroupPicker';
import { ITEM_LABELS, subitemsBadge } from './labels';
import '../TemplateEditor/TemplateEditor.css';

/** Attach board settings to stored columns (colours, options) once the board is known. */
function withBoardSettings(templateColumns, boardColumns) {
  const { idMap } = resolveColumns(templateColumns, boardColumns);
  return templateColumns.map((tc) => {
    const boardCol = boardColumns.find((bc) => String(bc.id) === String(idMap[String(tc.id)]));
    return { id: tc.id, title: tc.title, type: tc.type, settings: boardCol ? boardCol.settings : undefined };
  });
}

const rowOf = (columns, r) => ({ id: r.key, name: r.name, column_values: serializeRowValues(columns, r.values), updates: r.updates || [] });

export default function GroupTemplateEditor({ monday, itemId, boardId, template, onSave, onCancel, takenNames = [] }) {
  const isEdit = Boolean(template && template.id);
  // Opened from a board view there is no item (itemId undefined): every board
  // is reached from boardId instead.
  const itemBoard = useItemBoardColumns(monday, itemId, boardId);
  const subBoard = useBoardColumns(monday, itemId, boardId);
  const members = useBoardMembers(monday, itemId, boardId);
  const people = useAccountPeople(monday);

  const [name, setName] = useState(template ? template.name : '');
  const [accessLevel, setAccessLevel] = useState(template ? template.access_level : 'user_level');
  const [draft, setDraft] = useState(() => {
    const d = draftFromContent(template ? template.content : null);
    if (!d.items.length) d.items = [emptyItemDraft()];
    return d;
  });
  const [expanded, setExpanded] = useState(() => new Set());
  const [dirty, setDirty] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  // set by a refused Save; from then on unnamed items/subitems are outlined until fixed
  const [showRowErrors, setShowRowErrors] = useState(false);
  const nameInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [popover, setPopover] = useState(null); // { kind, level, itemKey?, subKey?, column?, anchorEl }
  const [panel, setPanel] = useState(null); // { level, itemKey, subKey? }
  const [confirm, setConfirm] = useState(null); // 'cancel' | 'capture'
  const [picker, setPicker] = useState(false); // the Import from board modal
  const [pendingCapture, setPendingCapture] = useState(null); // picker choice awaiting the replace confirmation
  const [capturing, setCapturing] = useState(false);
  const [captureNote, setCaptureNote] = useState(null); // truncation warning, or an error
  const itemSettingsMerged = useRef(false);
  const subSettingsMerged = useRef(false);

  // Boards arrived: attach their settings to the columns already in the draft.
  useEffect(() => {
    if (itemSettingsMerged.current || itemBoard.loading || itemBoard.error) return;
    itemSettingsMerged.current = true;
    setDraft((d) => ({ ...d, itemColumns: withBoardSettings(d.itemColumns, itemBoard.columns) }));
  }, [itemBoard.loading, itemBoard.error, itemBoard.columns]);
  useEffect(() => {
    if (subSettingsMerged.current || subBoard.loading || subBoard.error) return;
    subSettingsMerged.current = true;
    setDraft((d) => ({ ...d, subitemColumns: withBoardSettings(d.subitemColumns, subBoard.columns) }));
  }, [subBoard.loading, subBoard.error, subBoard.columns]);

  const touch = () => setDirty(true);
  const update = (fn) => {
    setDraft((d) => fn(d));
    touch();
  };
  const updateItem = (itemKey, fn) => update((d) => ({ ...d, items: d.items.map((it) => (it.key === itemKey ? fn(it) : it)) }));
  const updateSub = (itemKey, subKey, fn) =>
    updateItem(itemKey, (it) => ({ ...it, subitems: it.subitems.map((s) => (s.key === subKey ? fn(s) : s)) }));

  const counts = useMemo(() => countContent(contentFromDraft(draft)), [draft]);
  const unnamed = useMemo(() => unnamedGroupKeys(draft.items), [draft.items]);
  // a name another group template has is saved as "Name (1)" (lib/templateNames)
  const savedName = useMemo(() => uniqueTemplateName(name, takenNames), [name, takenNames]);
  const rowErrors = showRowErrors && unnamed.size > 0 ? unnamed : undefined;
  const blankUpdates = useMemo(() => blankUpdateGroupKeys(draft.items), [draft.items]);
  const updateErrors = showRowErrors && blankUpdates.size > 0 ? blankUpdates : undefined;
  const hasContent = name.trim() !== '' || draft.group.title.trim() !== '' || counts.items > 0 || draft.itemColumns.length > 0;
  const columnsFor = (level) => (level === 'item' ? draft.itemColumns : draft.subitemColumns);
  const boardFor = (level) => (level === 'item' ? itemBoard : subBoard);
  const usedIds = (level) => new Set(columnsFor(level).map((c) => String(c.id)));

  // ---- columns, per level ------------------------------------------------------
  const addColumns = (level, boardCols) => {
    const list = Array.isArray(boardCols) ? boardCols : [boardCols];
    const key = level === 'item' ? 'itemColumns' : 'subitemColumns';
    update((d) => {
      const have = new Set(d[key].map((c) => String(c.id)));
      return { ...d, [key]: [...d[key], ...list.filter((c) => !have.has(String(c.id)))] };
    });
  };
  const dropValues = (level, col, d) =>
    level === 'item'
      ? { ...d, items: d.items.map((it) => ({ ...it, values: omit(it.values, col.id) })) }
      : { ...d, items: d.items.map((it) => ({ ...it, subitems: it.subitems.map((s) => ({ ...s, values: omit(s.values, col.id) })) })) };
  const removeColumn = (level, col) => {
    const key = level === 'item' ? 'itemColumns' : 'subitemColumns';
    update((d) => dropValues(level, col, { ...d, [key]: d[key].filter((c) => String(c.id) !== String(col.id)) }));
  };
  const clearColumnValues = (level, col) => update((d) => dropValues(level, col, d));

  // ---- rows ----------------------------------------------------------------------
  const atItemLimit = draft.items.length >= GROUP_LIMITS.items;
  const addItem = () => {
    if (atItemLimit) return;
    update((d) => ({ ...d, items: [...d.items, emptyItemDraft()] }));
  };
  const addSubitem = (itemKey) =>
    updateItem(itemKey, (it) => (it.subitems.length >= GROUP_LIMITS.subitemsPerItem ? it : { ...it, subitems: [...it.subitems, emptySubitemDraft()] }));
  const toggleExpand = (itemKey) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });

  const setCell = (target, value) => {
    const put = (r) => {
      const values = { ...r.values };
      if (value === null || value === undefined) delete values[target.column.id];
      else values[target.column.id] = value;
      return { ...r, values };
    };
    if (target.level === 'item') updateItem(target.itemKey, put);
    else updateSub(target.itemKey, target.subKey, put);
  };
  const rowFor = (target) => {
    const it = draft.items.find((x) => x.key === target.itemKey);
    if (!it) return null;
    if (target.level === 'item') return it;
    return it.subitems.find((s) => s.key === target.subKey) || null;
  };
  const setUpdates = (target, updates) => {
    if (target.level === 'item') updateItem(target.itemKey, (it) => ({ ...it, updates }));
    else updateSub(target.itemKey, target.subKey, (s) => ({ ...s, updates }));
  };

  // ---- Import from board (F2.2, F2.3): replace the draft with a captured group ------------
  const runCapture = async ({ groupId, includeUpdates }) => {
    setConfirm(null);
    setPendingCapture(null);
    setCapturing(true);
    try {
      const { content, truncated, updatesRefused } = await captureGroup(monday, {
        boardId,
        groupId,
        includeUpdates,
        itemColumns: itemBoard.columns,
        subitemColumns: subBoard.columns,
      });
      let next = draftFromContent(content);
      // the boards are already in hand, so attach their settings right away
      next = {
        ...next,
        itemColumns: withBoardSettings(next.itemColumns, itemBoard.columns),
        subitemColumns: withBoardSettings(next.subitemColumns, subBoard.columns),
      };
      if (!next.items.length) next.items = [emptyItemDraft()];
      setDraft(next);
      setExpanded(new Set());
      if (name.trim() === '') setName(content.group.title);
      const notes = [truncationNote(truncated), updatesRefused && UPDATES_REFUSED_NOTE].filter(Boolean);
      setCaptureNote(notes.length ? notes.join(' ') : null);
      setPicker(false);
      touch();
    } catch (e) {
      setCaptureNote(importErrorNote(e));
      setPicker(false);
    } finally {
      setCapturing(false);
    }
  };
  const onCaptureChosen = (choice) => {
    // a draft with content gets a confirmation first, like Sync in the subitem editor
    if (counts.items > 0 || draft.itemColumns.length > 0) {
      setPendingCapture(choice);
      setConfirm('capture');
      return;
    }
    runCapture(choice);
  };

  // ---- save ------------------------------------------------------------------------
  const save = async () => {
    let bad = false;
    if (name.trim() === '') {
      setNameError(true);
      bad = true;
    }
    if (draft.group.title.trim() === '') {
      setTitleError(true);
      bad = true;
    }
    if (bad) {
      const ref = name.trim() === '' ? nameInputRef : titleInputRef;
      if (ref.current) ref.current.focus();
      return;
    }
    setNameError(false);
    setTitleError(false);
    // an item or subitem with values but no name, or an update left empty,
    // would be dropped without a word: refuse instead
    if (unnamed.size > 0 || blankUpdates.size > 0) {
      setShowRowErrors(true);
      // open the items whose flagged subitems would otherwise be hidden
      const flagged = (key) => unnamed.has(key) || blankUpdates.has(key);
      const parents = draft.items.filter((it) => it.subitems.some((s) => flagged(s.key))).map((it) => it.key);
      if (parents.length) setExpanded((prev) => new Set([...prev, ...parents]));
      // and the first empty update, unless its panel is already open
      const open = panel && (panel.level === 'item' ? panel.itemKey : panel.subKey);
      if (blankUpdates.size > 0 && !blankUpdates.has(open)) {
        const item = draft.items.find((it) => blankUpdates.has(it.key));
        if (item) setPanel({ level: 'item', itemKey: item.key });
        else {
          const parent = draft.items.find((it) => it.subitems.some((s) => blankUpdates.has(s.key)));
          setPanel({ level: 'subitem', itemKey: parent.key, subKey: parent.subitems.find((s) => blankUpdates.has(s.key)).key });
        }
      }
      return;
    }
    setSaving(true);
    try {
      await onSave({
        id: isEdit ? template.id : undefined,
        name: savedName,
        type: 'group',
        access_level: accessLevel,
        columns: [],
        subitems: [],
        content: contentFromDraft(draft),
      });
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    if (dirty) setConfirm('cancel');
    else onCancel();
  };

  // ---- render ----------------------------------------------------------------------
  const saveLabel = isEdit ? 'Save changes' : 'Save template';
  const heading = isEdit ? 'Edit group template' : 'New group template';
  const subheading = isEdit
    ? 'Change the group, its items, their subitems and updates — all saved together in one action.'
    : 'Author the group this template should add to a board: its items, their subitems, and the updates to post on each.';
  const boardsLoading = itemBoard.loading || subBoard.loading;
  const panelRow = panel ? rowFor(panel) : null;

  const nestedTable = (row) => {
    const it = draft.items.find((x) => x.key === row.id);
    if (!it) return null;
    return (
      <SubitemsTable
        columns={draft.subitemColumns}
        subitems={it.subitems.map((s) => rowOf(draft.subitemColumns, s))}
        readOnly={false}
        groupColor={draft.group.color}
        nested
        invalidNameIds={rowErrors}
        invalidUpdateIds={updateErrors}
        onNameChange={(subKey, v) => updateSub(it.key, subKey, (s) => ({ ...s, name: v }))}
        onRowDelete={(subKey) => updateItem(it.key, (x) => ({ ...x, subitems: x.subitems.filter((s) => s.key !== subKey) }))}
        onRowsReorder={(from, to) =>
          updateItem(it.key, (x) => {
            const next = [...x.subitems];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return { ...x, subitems: next };
          })
        }
        onAddRow={() => addSubitem(it.key)}
        onCellClick={(subKey, column, anchorEl) => {
          const target = { level: 'subitem', itemKey: it.key, subKey, column };
          if (canonicalType(column.type) === 'checkbox') {
            const s = rowFor(target);
            setCell(target, s && s.values[column.id] ? null : true);
            return;
          }
          setPopover({ kind: 'cell', ...target, anchorEl });
        }}
        onAddColumnClick={(anchorEl) => setPopover({ kind: 'addcol', level: 'subitem', anchorEl })}
        onColumnMenuClick={(column, anchorEl) => setPopover({ kind: 'colmenu', level: 'subitem', column, anchorEl })}
        onOpenUpdates={(subKey) => setPanel({ level: 'subitem', itemKey: it.key, subKey })}
      />
    );
  };

  return (
    <>
      <div>
        <div className="mb-6">
          <button
            onClick={cancel}
            className="inline-flex items-center mb-4 text-sm font-medium text-secondary transition-colors hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Templates
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-primary">{heading}</h1>
            {dirty && <span className="text-sm font-medium text-warning-ink">● Unsaved changes</span>}
          </div>
          <p className="mt-2 text-sm text-secondary">{subheading}</p>
        </div>

        <TemplateDetailsCard
          name={name}
          savedAs={savedName !== name.trim() ? savedName : null}
          nameError={nameError}
          nameInputRef={nameInputRef}
          accessLevel={accessLevel}
          onNameChange={(v) => {
            setName(v);
            if (v.trim() !== '') setNameError(false);
            touch();
          }}
          onAccessChange={(v) => {
            setAccessLevel(v);
            touch();
          }}
        />

        <GroupHeaderCard
          title={draft.group.title}
          color={draft.group.color}
          titleError={titleError}
          titleInputRef={titleInputRef}
          onTitleChange={(v) => {
            update((d) => ({ ...d, group: { ...d.group, title: v } }));
            if (v.trim() !== '') setTitleError(false);
          }}
          onColorChange={(c) => update((d) => ({ ...d, group: { ...d.group, color: c } }))}
        />

        {/* Items, each expanding into its subitems */}
        <div className="mb-6 bg-surface border border-subtle rounded-lg shadow-sm">
          <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-b border-subtle">
            <h2 className="spt-label">Items</h2>
            <span className="text-sm text-secondary">
              {counts.items} item{counts.items === 1 ? '' : 's'} · {counts.subitems} subitem{counts.subitems === 1 ? '' : 's'} ·{' '}
              {counts.updates} update{counts.updates === 1 ? '' : 's'}
            </span>
            {boardsLoading && (
              <span className="inline-flex items-center gap-2 text-sm text-secondary" role="status">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Reading this board and its subitems board
              </span>
            )}
            <span className="flex-1" />
            <button
              type="button"
              onClick={() => setPicker(true)}
              data-tour="import"
              disabled={!boardId || boardsLoading || capturing}
              title={
                !boardId
                  ? 'This view has no board context'
                  : boardsLoading
                    ? 'Reading this board and its subitems board'
                    : 'Replace the table with an existing group of this board'
              }
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-secondary rounded-md hover:bg-hover disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Import from board
            </button>
          </div>
          {captureNote && (
            <div className="px-6 py-3 text-sm border-b border-subtle text-primary bg-sunken" role="status">
              {captureNote}
            </div>
          )}
          {(itemBoard.error || subBoard.error) && (
            <div className="px-6 py-3 text-sm border-b border-subtle text-primary bg-sunken" role="alert">
              {!itemBoard.error && isNoSubitemsBoard(subBoard.error)
                ? `${NO_SUBITEMS_BOARD_HINT} Items and their values can still be authored and saved.`
                : `${itemBoard.error ? 'Could not read this board’s columns' : 'Could not read the subitems board’s columns'}, so colours and label lists are unavailable there. Your template’s values are still here and can be saved.`}
            </div>
          )}
          <div className="p-4" style={{ maxHeight: 560, overflowY: 'auto' }} data-tour="table">
            {/* the group as it will land on the board: its title in its colour */}
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-lg font-semibold" style={{ color: draft.group.color }}>
                {draft.group.title.trim() ? <TokenText value={draft.group.title} /> : 'Untitled group'}
              </span>
              <span className="text-sm text-secondary">
                {counts.items} item{counts.items === 1 ? '' : 's'}
              </span>
            </div>
            <SubitemsTable
              groupColor={draft.group.color}
              columns={draft.itemColumns}
              subitems={draft.items.map((it) => ({ ...rowOf(draft.itemColumns, it), subitems: it.subitems }))}
              readOnly={false}
              invalidNameIds={rowErrors}
              invalidUpdateIds={updateErrors}
              labels={ITEM_LABELS}
              subitemBadge={subitemsBadge}
              expandedIds={expanded}
              onToggleExpand={toggleExpand}
              renderExpanded={nestedTable}
              onNameChange={(itemKey, v) => updateItem(itemKey, (it) => ({ ...it, name: v }))}
              onRowDelete={(itemKey) => update((d) => ({ ...d, items: d.items.filter((it) => it.key !== itemKey) }))}
              onAddRow={atItemLimit ? undefined : addItem}
              onCellClick={(itemKey, column, anchorEl) => {
                const target = { level: 'item', itemKey, column };
                if (canonicalType(column.type) === 'checkbox') {
                  const it = rowFor(target);
                  setCell(target, it && it.values[column.id] ? null : true);
                  return;
                }
                setPopover({ kind: 'cell', ...target, anchorEl });
              }}
              onAddColumnClick={(anchorEl) => setPopover({ kind: 'addcol', level: 'item', anchorEl })}
              onColumnMenuClick={(column, anchorEl) => setPopover({ kind: 'colmenu', level: 'item', column, anchorEl })}
              onOpenUpdates={(itemKey) => setPanel({ level: 'item', itemKey })}
            />
            {rowErrors && (
              <p className="mt-2 text-sm text-danger-ink" role="alert">
                {unnamedRowsMessage(rowErrors.size)}
              </p>
            )}
            {updateErrors && (
              <p className="mt-2 text-sm text-danger-ink" role="alert">
                {blankUpdatesMessage(updateErrors.size)}
              </p>
            )}
            <p className="mt-2 text-xs text-secondary">
              Blank rows are ignored on save; every row with values or subitems needs a name. Expand an item to author its subitems. Limits: {GROUP_LIMITS.items} items,{' '}
              {GROUP_LIMITS.subitemsPerItem} subitems per item, {GROUP_LIMITS.updatesPerRow} updates per row.
              {atItemLimit ? ' This template has reached its item limit.' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary transition-colors bg-surface border border-strong rounded-md shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            data-tour="save"
            disabled={saving || !hasContent}
            aria-busy={saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-on-accent transition-colors bg-accent border border-transparent rounded-md shadow-sm hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>

      {/* Popovers, bound to the level they were opened from */}
      {popover && popover.kind === 'addcol' && (
        <AddColumnMenu
          anchorEl={popover.anchorEl}
          caption={popover.level === 'item' ? 'Columns on this board' : 'Columns on the subitems board'}
          boardColumns={boardFor(popover.level).columns}
          loading={boardFor(popover.level).loading}
          error={boardFor(popover.level).error}
          usedIds={usedIds(popover.level)}
          onAdd={(cols) => addColumns(popover.level, cols)}
          onRemove={(col) => removeColumn(popover.level, col)}
          onClose={() => setPopover(null)}
        />
      )}
      {popover && popover.kind === 'colmenu' && (
        <ColumnMenu
          anchorEl={popover.anchorEl}
          column={popover.column}
          onClearValues={(col) => clearColumnValues(popover.level, col)}
          onRemoveColumn={(col) => removeColumn(popover.level, col)}
          onClose={() => setPopover(null)}
        />
      )}
      {popover && popover.kind === 'cell' && (
        <CellEditor
          anchorEl={popover.anchorEl}
          column={columnsFor(popover.level).find((c) => String(c.id) === String(popover.column.id)) || popover.column}
          boardLoading={boardFor(popover.level).loading}
          boardError={Boolean(boardFor(popover.level).error)}
          users={members.users}
          usersError={members.error}
          usersLoading={members.status !== 'loaded'}
          onNeedUsers={members.load}
          value={(rowFor(popover) || { values: {} }).values[popover.column.id]}
          onChange={(v) => setCell(popover, v)}
          onClose={() => setPopover(null)}
        />
      )}

      {panelRow && (
        <UpdatesPanel
          row={{ id: panelRow.key, name: panelRow.name, updates: panelRow.updates }}
          limits={GROUP_LIMITS}
          people={people}
          showErrors={showRowErrors}
          onChange={(updates) => setUpdates(panel, updates)}
          onClose={() => setPanel(null)}
        />
      )}

      <GroupPicker
        open={picker}
        api={monday}
        boardId={boardId}
        capturing={capturing}
        onCapture={onCaptureChosen}
        onClose={() => !capturing && setPicker(false)}
      />

      {/* Confirm: capture replaces the table */}
      <Modal show={confirm === 'capture'} onClose={() => setConfirm(null)} title="Replace template content?">
        <ModalContent>
          <p>The imported group will replace this template's current group, items, subitems, and values.</p>
        </ModalContent>
        <ModalFooter>
          <div className="flex justify-end gap-3">
            <Button kind={Button.kinds.SECONDARY} onClick={() => setConfirm(null)}>
              Keep current content
            </Button>
            <Button onClick={() => pendingCapture && runCapture(pendingCapture)}>Replace</Button>
          </div>
        </ModalFooter>
      </Modal>

      <Modal show={confirm === 'cancel'} onClose={() => setConfirm(null)} title="Discard changes?">
        <ModalContent>
          <p>Your group, items, subitems, values and updates will be lost.</p>
        </ModalContent>
        <ModalFooter>
          <div className="flex justify-end gap-3">
            <Button kind={Button.kinds.SECONDARY} onClick={() => setConfirm(null)}>
              Keep editing
            </Button>
            <Button color={Button.colors.NEGATIVE} onClick={onCancel}>
              Discard
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
}

function omit(values, key) {
  const next = { ...(values || {}) };
  delete next[key];
  return next;
}
